import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { TrendingUp, DollarSign, Percent, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const ProfitabilityPage = () => {
    const { api } = useAuth();
    const [profitability, setProfitability] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [profitRes, categoriesRes] = await Promise.all([
                api().get('/reports/profitability'),
                api().get('/categories')
            ]);
            setProfitability(profitRes.data);
            setCategories(categoriesRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const filteredData = profitability.filter(p => {
        if (filterCategory === 'all') return true;
        const category = categories.find(c => c.name === p.category_name);
        return category?.id === filterCategory;
    });

    // Calculate totals
    const totals = filteredData.reduce((acc, p) => ({
        stockValue: acc.stockValue + p.stock_value,
        potentialRevenue: acc.potentialRevenue + p.potential_revenue,
        avgMargin: acc.avgMargin + p.margin_percentage
    }), { stockValue: 0, potentialRevenue: 0, avgMargin: 0 });

    const avgMargin = filteredData.length > 0 ? totals.avgMargin / filteredData.length : 0;

    // Chart data - top 10 by margin
    const chartData = [...filteredData]
        .sort((a, b) => b.margin_percentage - a.margin_percentage)
        .slice(0, 10)
        .map(p => ({
            name: p.product_name.length > 15 ? p.product_name.substring(0, 15) + '...' : p.product_name,
            margin: p.margin_percentage,
            grossMargin: p.gross_margin
        }));

    const getMarginColor = (margin) => {
        if (margin >= 50) return '#4A7C59';
        if (margin >= 30) return '#E07A5F';
        return '#D62828';
    };

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="profitability-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Rentabilité
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Analyse des marges par produit
                    </p>
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-48" data-testid="profitability-category-filter">
                        <SelectValue placeholder="Toutes catégories" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Toutes catégories</SelectItem>
                        {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="card-hover">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Valeur Stock</p>
                                <p className="text-2xl font-display font-bold text-foreground mt-1">
                                    {totals.stockValue.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} CFA
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-accent" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-hover">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Revenu Potentiel</p>
                                <p className="text-2xl font-display font-bold text-accent mt-1">
                                    {totals.potentialRevenue.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} CFA
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-accent" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-hover">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Marge Moyenne</p>
                                <p className="text-2xl font-display font-bold text-foreground mt-1">
                                    {avgMargin.toFixed(1)}%
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                                <Percent className="w-5 h-5 text-accent" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="font-display text-lg">Top 10 - Marges les plus élevées</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                                    <XAxis type="number" unit="%" stroke="#666666" fontSize={12} />
                                    <YAxis 
                                        dataKey="name" 
                                        type="category" 
                                        width={120} 
                                        stroke="#666666" 
                                        fontSize={12}
                                        tickLine={false}
                                    />
                                    <Tooltip 
                                        formatter={(value) => [`${value.toFixed(1)}%`, 'Marge']}
                                        contentStyle={{ 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #E5E5E5',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Bar dataKey="margin" radius={[0, 4, 4, 0]}>
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={getMarginColor(entry.margin)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Table */}
            <Card>
                <div className="rounded-xl border border-border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/30">
                                <TableHead className="font-medium">Produit</TableHead>
                                <TableHead className="font-medium">Catégorie</TableHead>
                                <TableHead className="font-medium text-right">Prix Achat</TableHead>
                                <TableHead className="font-medium text-right">Prix Vente</TableHead>
                                <TableHead className="font-medium text-right">Marge Brute</TableHead>
                                <TableHead className="font-medium text-right">% Marge</TableHead>
                                <TableHead className="font-medium text-right">Stock</TableHead>
                                <TableHead className="font-medium text-right">Valeur Stock</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-12">
                                        <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                                        <p className="text-muted-foreground">Aucune donnée</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredData.map((item) => (
                                    <TableRow key={item.product_id} className="hover:bg-muted/20" data-testid={`profit-row-${item.product_id}`}>
                                        <TableCell className="font-medium">{item.product_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="bg-muted/50">
                                                {item.category_name}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {item.purchase_price.toLocaleString('fr-FR')} CFA
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {item.sale_price.toLocaleString('fr-FR')} CFA
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-accent font-semibold">
                                            {item.gross_margin.toLocaleString('fr-FR')} CFA
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Badge 
                                                variant="outline"
                                                style={{ 
                                                    backgroundColor: `${getMarginColor(item.margin_percentage)}15`,
                                                    color: getMarginColor(item.margin_percentage),
                                                    borderColor: `${getMarginColor(item.margin_percentage)}30`
                                                }}
                                            >
                                                {item.margin_percentage.toFixed(1)}%
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {item.stock_quantity}
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-semibold">
                                            {item.stock_value.toLocaleString('fr-FR')} CFA
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

export default ProfitabilityPage;
